<script setup>
import { computed } from 'vue';

import WootLabel from 'dashboard/components-next/label/Label.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';
import { REDACTED_KEYS } from './utils/automationOperationsConstants';
import { selectedActionPayloadOf } from './utils/automationOperationsFormatters';

const props = defineProps({
  action: { type: Object, required: true },
});

const { eventLabel, formatDate, formatStatus } =
  useAutomationOperationsFormatters();

const selectedActionAttempts = computed(() => props.action?.attempts || []);
const selectedActionReceipts = computed(() => props.action?.receipts || []);
const selectedActionApprovals = computed(() => props.action?.approvals || []);
const selectedActionPayload = computed(() =>
  selectedActionPayloadOf(props.action)
);
</script>

<template>
  <div
    class="mt-4 grid gap-3 border-t border-n-weak pt-4"
    data-test="automation-action-drilldown"
  >
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-heading-3 text-n-slate-12">
        {{ action.action_ref }}
      </span>
      <WootLabel
        :label="formatStatus(action.action_name)"
        color="slate"
        compact
      />
    </div>
    <div class="grid gap-3 xl:grid-cols-3">
      <div class="min-w-0">
        <div class="mb-2 text-label-small text-n-slate-11">
          {{ formatStatus('attempts') }}
        </div>
        <div class="grid gap-2">
          <div
            v-for="attempt in selectedActionAttempts"
            :key="attempt.id"
            class="grid gap-1 rounded-md border border-n-weak px-3 py-2 text-body-main text-n-slate-11"
          >
            <div class="flex min-w-0 justify-between gap-2">
              <span class="truncate text-n-slate-12">
                {{ formatStatus(attempt.result) }}
              </span>
              <span class="tabular-nums">
                {{ eventLabel(attempt.attempt_number || attempt.id) }}
              </span>
            </div>
            <span v-if="attempt.error_class" class="truncate">
              {{ attempt.error_class }}
            </span>
            <span v-if="attempt.error_kind" class="truncate">
              {{ formatStatus(attempt.error_kind) }}
            </span>
            <span v-if="attempt.blocked_reason" class="truncate">
              {{ formatStatus(attempt.blocked_reason) }}
            </span>
            <span v-if="attempt.suppressed_reason" class="truncate">
              {{ formatStatus(attempt.suppressed_reason) }}
            </span>
            <span v-if="attempt.idempotency_key_digest" class="break-all">
              {{ attempt.idempotency_key_digest }}
            </span>
          </div>
          <div
            v-if="!selectedActionAttempts.length"
            class="rounded-md bg-n-slate-2 px-3 py-2 text-body-main text-n-slate-11"
          >
            {{ $t('AUTOMATION.OPERATIONS.UNAVAILABLE') }}
          </div>
        </div>
      </div>

      <div class="min-w-0">
        <div class="mb-2 text-label-small text-n-slate-11">
          {{ formatStatus('receipts') }}
        </div>
        <div class="grid gap-2">
          <div
            v-for="receipt in selectedActionReceipts"
            :key="receipt.id"
            class="grid gap-1 rounded-md border border-n-weak px-3 py-2 text-body-main text-n-slate-11"
          >
            <div class="flex min-w-0 justify-between gap-2">
              <span class="truncate text-n-slate-12">
                {{ formatStatus(receipt.receipt_kind) }}
              </span>
              <span class="truncate">
                {{ receipt.provider || '-' }}
              </span>
            </div>
            <span v-if="receipt.observed_state" class="truncate">
              {{ formatStatus(receipt.observed_state) }}
            </span>
            <span v-if="receipt.effect_type" class="truncate">
              {{ formatStatus(receipt.effect_type) }}
            </span>
            <span v-if="receipt.idempotency_key_digest" class="break-all">
              {{ receipt.idempotency_key_digest }}
            </span>
            <span>{{ formatDate(receipt.observed_at) }}</span>
          </div>
          <div
            v-if="!selectedActionReceipts.length"
            class="rounded-md bg-n-slate-2 px-3 py-2 text-body-main text-n-slate-11"
          >
            {{ $t('AUTOMATION.OPERATIONS.UNAVAILABLE') }}
          </div>
        </div>
      </div>

      <div class="min-w-0">
        <div class="mb-2 text-label-small text-n-slate-11">
          {{ formatStatus('approvals') }}
        </div>
        <div class="grid gap-2">
          <div
            v-for="approval in selectedActionApprovals"
            :key="approval.id"
            class="grid gap-1 rounded-md border border-n-weak px-3 py-2 text-body-main text-n-slate-11"
          >
            <div class="flex min-w-0 justify-between gap-2">
              <span class="truncate text-n-slate-12">
                {{ formatStatus(approval.status) }}
              </span>
              <span class="truncate">
                {{ formatStatus(approval.risk_level) }}
              </span>
            </div>
            <span v-if="approval.request_reason" class="truncate">
              {{ approval.request_reason }}
            </span>
            <span v-if="approval.reviewer_id" class="tabular-nums">
              {{ eventLabel(approval.reviewer_id) }}
            </span>
            <span>
              {{ formatDate(approval.reviewed_at || approval.requested_at) }}
            </span>
          </div>
          <div
            v-if="!selectedActionApprovals.length"
            class="rounded-md bg-n-slate-2 px-3 py-2 text-body-main text-n-slate-11"
          >
            {{ $t('AUTOMATION.OPERATIONS.UNAVAILABLE') }}
          </div>
        </div>
      </div>
    </div>
    <LaisPayloadViewer
      v-if="selectedActionPayload"
      :payload="selectedActionPayload"
      :title="$t('AUTOMATION.OPERATIONS.EXECUTION.PAYLOAD_TITLE')"
      :redacted-keys="REDACTED_KEYS"
    />
  </div>
</template>
